/**
 * Local emoji dataset — fully offline (SPEC §1.2 minimal metadata, NFR-PWA-002).
 * Each entry: [emoji, space-separated search keywords]. No remote fonts/packs.
 */

export interface EmojiGroup {
  id: string;
  label: string;
  icon: string;
  emojis: string[];
  keywords: Map<string, string>;
}

type Row = [string, string];

const SMILEYS: Row[] = [
  ["😀", "grin happy smile joy face"],
  ["😃", "smile happy open mouth joy"],
  ["😄", "laugh smile happy eyes joy"],
  ["😁", "beam grin teeth happy"],
  ["😆", "lol laugh squint haha"],
  ["😅", "sweat laugh relief nervous"],
  ["🤣", "rofl roll floor laughing crying"],
  ["😂", "joy tears laugh crying lol"],
  ["🙂", "slight smile friendly"],
  ["🙃", "upside down silly sarcasm"],
  ["🫠", "melting heat sarcasm"],
  ["😉", "wink flirt"],
  ["😊", "blush shy smile warm"],
  ["😇", "angel halo innocent good"],
  ["🥰", "love hearts adore in love"],
  ["😍", "heart eyes love crush"],
  ["🤩", "star struck wow amazed"],
  ["😘", "kiss blow love"],
  ["😗", "kissing"],
  ["🥺", "pleading puppy eyes begging"],
  ["😚", "kissing closed eyes"],
  ["🤗", "hug embrace warm"],
  ["🤭", "giggle oops hand mouth"],
  ["🫢", "gasp surprise hand mouth"],
  ["🤫", "shush secret quiet"],
  ["🤔", "think hmm considering"],
  ["🫡", "salute respect yes sir"],
  ["🤐", "zipper mouth silence"],
  ["🤨", "eyebrow raise suspicious"],
  ["😐", "neutral meh blank"],
  ["😑", "expressionless blank dead inside"],
  ["😶", "no mouth speechless silent"],
  ["🫥", "dotted invisible faded lonely"],
  ["😏", "smirk sly smug"],
  ["😒", "unamused meh annoyed"],
  ["🙄", "eye roll annoyed whatever"],
  ["😬", "grimace awkward yikes"],
  ["🤥", "lying pinocchio liar"],
  ["😌", "relieved calm relaxed content"],
  ["😔", "pensive sad down"],
  ["😪", "sleepy tired sleepy face"],
  ["🤤", "drooling hungry desire"],
  ["😴", "sleep zzz asleep tired"],
  ["😷", "mask sick covid careful"],
  ["🤒", "thermometer sick fever ill"],
  ["🤕", "bandage hurt injured ouch"],
  ["🤢", "nauseated sick green gross"],
  ["🤮", "vomit sick puke gross"],
  ["🤧", "sneeze sick cold tissue"],
  ["🥵", "hot heat sweating summer"],
  ["🥶", "cold freezing winter ice"],
  ["🥴", "woozy dizzy drunk tipsy"],
  ["😵", "knocked out dizzy dead"],
  ["🤯", "mind blown explode shocked"],
  ["🤠", "cowboy hat yeehaw"],
  ["🥳", "party celebrate birthday yey"],
  ["🥸", "disguise incognito spy glasses"],
  ["😎", "cool sunglasses sunglasses chill"],
  ["🤓", "nerd glasses geek smart"],
  ["🧐", "monocle inspect fancy curious"],
  ["😕", "confused unsure hmm"],
  ["🫤", "diagonal mouth conflicted meh"],
  ["😟", "worried concerned"],
  ["🙁", "frown sad slight"],
  ["😮", "oh wow surprise open mouth"],
  ["😯", "hushed surprised quiet"],
  ["😲", "astonished shocked wow"],
  ["🥹", "holding back tears touched teary"],
  ["😳", "flushed embarrassed blush cringe"],
  ["😦", "frowning open mouth shock"],
  ["😧", "anguished worried shock"],
  ["😨", "fearful scared afraid"],
  ["😰", "anxious nervous sweat scared"],
  ["😥", "sad relieved phew"],
  ["😢", "cry sad tear"],
  ["😭", "sob bawl crying loud tears"],
  ["😱", "scream fear horror shock"],
  ["😖", "confounded frustrated ugh"],
  ["😣", "persevere struggling ugh"],
  ["😞", "disappointed sad let down"],
  ["😓", "downcast sweat tired work"],
  ["😩", "weary exhausted drained"],
  ["😫", "tired exhausted done"],
  ["🥱", "yawn bored sleepy"],
  ["😤", "triumph steam determined huff"],
  ["😡", "rage angry mad furious"],
  ["😠", "angry mad upset"],
  ["🤬", "swearing cursing symbols angry"],
  ["😈", "devil smirk evil mischievous"],
  ["👿", "imp angry devil evil"],
  ["💀", "skull dead deadpan dying lol"],
  ["☠️", "skull crossbones danger poison dead"],
  ["💩", "poop pile crap"],
  ["🤡", "clown circus creepy"],
  ["👹", "ogre demon monster red"],
  ["👺", "goblin mask creature"],
  ["👻", "ghost boo halloween spooky"],
  ["👽", "alien ufo outer space"],
  ["🤖", "robot bot ai machine"],
  ["😺", "cat happy smile"],
  ["😹", "cat joy laugh"],
  ["😻", "cat heart eyes love"],
  ["😼", "cat smirk sly"],
  ["😽", "cat kiss"],
  ["🙈", "see no evil monkey shy oops"],
  ["🙉", "hear no evil monkey ignore"],
  ["🙊", "speak no evil monkey secret quiet"],
];

const GESTURES: Row[] = [
  ["👋", "wave hello hi bye hand"],
  ["🤚", "raised back hand stop"],
  ["🖐️", "hand splayed fingers five"],
  ["✋", "raised hand stop high five"],
  ["🖖", "vulcan spock star trek"],
  ["🫱", "right hand handshake offer"],
  ["🫲", "left hand handshake offer"],
  ["👌", "ok perfect nice pinched"],
  ["🤌", "pinched fingers italian chef kiss"],
  ["🤏", "pinch small tiny little"],
  ["✌️", "victory peace two deuce"],
  ["🤞", "crossed fingers luck hope"],
  ["🫰", "finger heart snap kpop love money"],
  ["🤟", "love you sign hand"],
  ["🤘", "rock horns metal devil"],
  ["🤙", "call me shaka hang loose"],
  ["👈", "point left that one"],
  ["👉", "point right this one"],
  ["👆", "point up above"],
  ["🖕", "middle finger rude"],
  ["👇", "point down below"],
  ["☝️", "index up one"],
  ["🫵", "point at you accusation"],
  ["👍", "thumbs up like good yes approve"],
  ["👎", "thumbs down dislike bad no"],
  ["✊", "fist bump power solidarity"],
  ["👊", "punch fist bro bump"],
  ["👏", "clap applause bravo well done"],
  ["🙌", "raising hands celebrate praise hooray"],
  ["🫶", "heart hands love appreciation"],
  ["👐", "open hands hug give"],
  ["🤲", "palms up prayer offer humble"],
  ["🤝", "handshake deal agree partner"],
  ["🙏", "pray thanks please please gratitude"],
  ["✍️", "writing hand note sign"],
  ["💅", "nails polish pampered fabulous"],
  ["🤳", "selfie phone camera"],
  ["💪", "muscle flex strong bicep gym"],
  ["🦾", "mechanical arm prosthetic strength"],
  ["🧠", "brain smart mind think"],
  ["👀", "eyes look watching watching you"],
  ["👁️", "eye see watch"],
  ["👅", "tongue tasty lick"],
  ["👄", "mouth lips kiss"],
  ["🦷", "tooth dentist"],
  ["🫀", "anatomical heart organ pulse"],
  ["🫁", "lungs breathe"],
];

const HEARTS: Row[] = [
  ["❤️", "red heart love"],
  ["🧡", "orange heart love"],
  ["💛", "yellow heart love friendship"],
  ["💚", "green heart love nature"],
  ["💙", "blue heart love loyalty"],
  ["💜", "purple heart love violet"],
  ["🖤", "black heart dark love"],
  ["🤍", "white heart pure love"],
  ["🤎", "brown heart love"],
  ["💔", "broken heart heartbreak sad"],
  ["❣️", "heart exclamation love"],
  ["💕", "two hearts love couple"],
  ["💞", "revolving hearts love spinning"],
  ["💓", "beating heart love pulse"],
  ["💗", "growing heart love"],
  ["💖", "sparkling heart love shine"],
  ["💘", "heart arrow cupid valentine"],
  ["💝", "heart ribbon gift love"],
  ["💟", "heart decoration purple"],
  ["❤️‍🔥", "heart on fire passion burning love"],
  ["❤️‍🩹", "mending heart healing recover"],
  ["💋", "kiss mark lipstick love"],
  ["🫂", "people hugging support comfort"],
  ["👩‍❤️‍👨", "couple love relationship"],
  ["😍", "love heart eyes"],
  ["✨", "sparkles shiny magic stars glitter"],
];

const ANIMALS: Row[] = [
  ["🐶", "dog puppy pet woof"],
  ["🐱", "cat kitten meow pet"],
  ["🐭", "mouse squeak tiny"],
  ["🐹", "hamster pet cute"],
  ["🐰", "rabbit bunny hop easter"],
  ["🦊", "fox sly clever"],
  ["🐻", "bear grizzly strong"],
  ["🐼", "panda china cute bamboo"],
  ["🐻‍❄️", "polar bear arctic white"],
  ["🐨", "koala australia cute"],
  ["🐯", "tiger roar stripes"],
  ["🦁", "lion roar king brave"],
  ["🐮", "cow moo farm"],
  ["🐷", "pig oink farm"],
  ["🐸", "frog ribbit pepe"],
  ["🐵", "monkey face banana"],
  ["🙈", "monkey shy see no evil"],
  ["🙉", "monkey hear no evil"],
  ["🙊", "monkey speak no evil secret"],
  ["🐔", "chicken hen farm cluck"],
  ["🐧", "penguin antarctica cute waddle"],
  ["🐦", "bird tweet fly"],
  ["🐤", "chick baby bird cute"],
  ["🦆", "duck quack pond"],
  ["🦅", "eagle bald freedom"],
  ["🦉", "owl night wise hoot"],
  ["🦇", "bat vampire night"],
  ["🐺", "wolf howl pack"],
  ["🐗", "boar pig tusk"],
  ["🐴", "horse neigh ride"],
  ["🦄", "unicorn magic rainbow fantasy"],
  ["🐝", "bee honey buzz busy"],
  ["🪲", "beetle bug insect"],
  ["🦋", "butterfly transform pretty"],
  ["🐌", "snail slow"],
  ["🐞", "ladybug luck insect"],
  ["🐜", "ant small work"],
  ["🕷️", "spider web halloween creepy"],
  ["🦂", "scorpion sting desert"],
  ["🐢", "turtle slow tortoise"],
  ["🐍", "snake snek hiss python"],
  ["🦖", "t-rex dinosaur rawr"],
  ["🦕", "sauropod dinosaur long neck"],
  ["🐙", "octopus eight arms sea"],
  ["🦑", "squid sea ink"],
  ["🦐", "shrimp seafood"],
  ["🦀", "crab pinch sea"],
  ["🐡", "blowfish puffer sea"],
  ["🐠", "tropical fish reef colorful"],
  ["🐟", "fish sea swim"],
  ["🐬", "dolphin smart sea jump"],
  ["🐳", "whale big sea spout"],
  ["🦈", "shark jaws danger sea"],
  ["🐊", "crocodile snap swamp"],
  ["🦓", "zebra stripes africa"],
  ["🦍", "gorilla strong king kong"],
  ["🐘", "elephant big memory"],
  ["🦒", "giraffe tall neck"],
  ["🦔", "hedgehog spiky cute"],
];

const FOOD: Row[] = [
  ["🍏", "green apple fruit healthy"],
  ["🍎", "red apple fruit teacher"],
  ["🍐", "pear fruit"],
  ["🍊", "orange tangerine fruit juice"],
  ["🍋", "lemon sour citrus"],
  ["🍌", "banana potassium monkey"],
  ["🍉", "watermelon summer juicy fruit"],
  ["🍇", "grapes fruit wine"],
  ["🍓", "strawberry berry sweet red"],
  ["🫐", "blueberries berry"],
  ["🍈", "melon melon cantaloupe"],
  ["🍒", "cherries fruit red"],
  ["🍑", "peach fruit booty"],
  ["🥭", "mango tropical fruit"],
  ["🍍", "pineapple tropical pizza debate"],
  ["🥥", "coconut tropical"],
  ["🥝", "kiwi fruit green"],
  ["🍅", "tomato fruit vegetable sauce"],
  ["🍆", "eggplant aubergine purple"],
  ["🥑", "avocado toast healthy"],
  ["🥦", "broccoli healthy green"],
  ["🥬", "leafy greens lettuce salad"],
  ["🥒", "cucumber pickle salad"],
  ["🌽", "corn cob"],
  ["🥕", "carrot orange healthy"],
  ["🧄", "garlic flavor"],
  ["🧅", "onion layers cry"],
  ["🥔", "potato spud chips"],
  ["🍠", "sweet potato roast"],
  ["🥐", "croissant french breakfast"],
  ["🍞", "bread loaf bakery"],
  ["🥖", "baguette french bread"],
  ["🥨", "pretzel twisted snack"],
  ["🧀", "cheese wedge yum"],
  ["🥚", "egg breakfast protein"],
  ["🍳", "cooking fried eggs breakfast"],
  ["🧇", "waffle breakfast syrup"],
  ["🥞", "pancakes stack breakfast syrup"],
  ["🥓", "bacon strips sizzle"],
  ["🍔", "burger hamburger fast food"],
  ["🍟", "fries fast food chips"],
  ["🍕", "pizza slice italian cheese"],
  ["🌭", "hot dog sausage"],
  ["🥪", "sandwich lunch"],
  ["🌮", "taco mexican tuesday"],
  ["🌯", "burrito wrap mexican"],
  ["🫔", "tamale mexican"],
  ["🥙", "stuffed flatbread kebab pita"],
  ["🧆", "falafel middle eastern"],
  ["🍜", "noodles ramen soup bowl"],
  ["🍲", "stew pot soup warm"],
  ["🍛", "curry rice spicy"],
  ["🍣", "sushi japanese fish rice"],
  ["🍤", "fried shrimp tempura seafood"],
  ["🍚", "rice bowl asian"],
  ["🍥", "fish cake naruto swirl"],
  ["🥟", "dumpling gyoza potsticker"],
  ["🍢", "oden skewer"],
  ["🍡", "dango sweet skewer"],
  ["🥠", "fortune cookie luck"],
  ["🍦", "ice cream sweet dessert"],
  ["🍩", "donut sweet sprinkles"],
  ["🍪", "cookie sweet crumbs chocolate chip"],
  ["🎂", "birthday cake celebrate party"],
  ["🍰", "cake slice dessert shortcake"],
  ["🧁", "cupcake sweet frosting"],
  ["🥧", "pie dessert apple"],
  ["🍫", "chocolate bar sweet cocoa"],
  ["🍬", "candy sweet sugar"],
  ["🍭", "lollipop sweet swirl"],
  ["🍯", "honey pot bee sweet"],
  ["🍼", "baby bottle milk"],
  ["🥛", "milk glass drink"],
  ["☕", "coffee hot tea morning caffeine"],
  ["🫖", "teapot tea brewing"],
  ["🍵", "tea green matcha calm"],
  ["🧋", "boba bubble tea pearls"],
  ["🧃", "juice box drink"],
  ["🥤", "soda cup straw drink"],
  ["🍺", "beer cheers drink pub"],
  ["🍻", "beers cheers clink friends"],
  ["🥂", "champagne clink celebrate toast"],
  ["🍷", "wine glass red classy"],
  ["🥃", "whisky tumbler spirits"],
  ["🍸", "cocktail martini bar"],
  ["🍹", "tropical drink rum punch vacation"],
  ["🧉", "mate drink south america"],
  ["🍿", "popcorn movie snack"],
  ["🧂", "salt seasoning"],
  ["🥜", "peanuts nut snack"],
  ["🌰", "chestnut autumn"],
  ["🌶️", "hot pepper spicy chili"],
];

const ACTIVITIES: Row[] = [
  ["⚽", "soccer football goal sport"],
  ["🏀", "basketball hoop sport bball"],
  ["🏈", "american football touchdown sport"],
  ["⚾", "baseball home run sport"],
  ["🥎", "softball sport"],
  ["🎾", "tennis racket sport match"],
  ["🏐", "volleyball sport beach"],
  ["🏉", "rugby sport tackle"],
  ["🥏", "frisbee ultimate sport"],
  ["🎱", "pool billiards eight ball game"],
  ["🏓", "ping pong table tennis sport"],
  ["🏸", "badminton racket sport"],
  ["🥊", "boxing glove fight punch"],
  ["🥋", "martial arts judo karate belt"],
  ["⛳", "golf flag sport hole"],
  ["🏹", "archery bow arrow target"],
  ["🎣", "fishing rod catch"],
  ["🤿", "diving mask snorkel scuba"],
  ["🎿", "ski winter snow sport"],
  ["🛷", "sled winter snow"],
  ["🥌", "curling stone ice sport"],
  ["🎯", "dart bullseye target hit"],
  ["🪀", "yo-yo toy play"],
  ["🪁", "kite fly wind"],
  ["🎮", "game controller gaming console play"],
  ["🕹️", "joystick arcade retro game"],
  ["🎲", "dice random game luck roll"],
  ["🧩", "puzzle piece solve jigsaw"],
  ["♟️", "chess pawn strategy game"],
  ["🎳", "bowling strike sport game"],
  ["🎭", "theater drama masks act"],
  ["🎨", "art palette paint create"],
  ["🎤", "microphone sing karaoke mic drop"],
  ["🎧", "headphones music listen audio"],
  ["🎼", "music notes score sheet"],
  ["🎵", "music note song tune"],
  ["🎶", "music notes melody jam"],
  ["🎹", "piano keys music keyboard"],
  ["🥁", "drum beat rhythm band"],
  ["🎸", "guitar rock band strings"],
  ["🎺", "trumpet brass band"],
  ["🎻", "violin classical strings orchestra"],
  ["🏆", "trophy win champion first prize"],
  ["🥇", "gold medal first winner"],
  ["🥈", "silver medal second"],
  ["🥉", "bronze medal third"],
  ["🎖️", "military medal honor ribbon"],
  ["🎪", "circus tent show"],
  ["🎉", "party popper celebrate congrats tada"],
  ["🎊", "confetti celebrate party spinner"],
  ["🎁", "gift present birthday surprise"],
  ["🎈", "balloon party float celebrate"],
  ["🚀", "rocket launch ship fast startup"],
];

const TRAVEL: Row[] = [
  ["🚗", "car drive vehicle road"],
  ["🚕", "taxi cab ride hailing"],
  ["🚙", "suv car adventure"],
  ["🚌", "bus public transport"],
  ["🚎", "trolley bus transport"],
  ["🏎️", "race car fast speed f1"],
  ["🚓", "police car siren cop"],
  ["🚑", "ambulance emergency medical"],
  ["🚒", "fire truck emergency"],
  ["🚐", "van minibus camper"],
  ["🛻", "pickup truck haul"],
  ["🚚", "truck delivery lorry"],
  ["🚛", "articulated lorry semi truck"],
  ["🚜", "tractor farm agriculture"],
  ["🛴", "kick scooter ride"],
  ["🚲", "bicycle bike cycle ride"],
  ["🛵", "motor scooter moped delivery"],
  ["🏍️", "motorcycle moto ride fast"],
  ["🚨", "siren alert emergency police light"],
  ["🚔", "police car front"],
  ["🚇", "metro subway underground train"],
  ["🚆", "train rail travel"],
  ["🚄", "bullet train fast shinkansen"],
  ["🚅", "high speed train tgv"],
  ["🚂", "steam locomotive train vintage"],
  ["✈️", "airplane flight travel vacation"],
  ["🛫", "takeoff departure flight travel"],
  ["🛬", "landing arrival flight"],
  ["🚁", "helicopter chopper fly"],
  ["🛶", "canoe paddle water"],
  ["⛵", "sailboat sailing wind yacht"],
  ["🚤", "speedboat fast water"],
  ["🛳️", "passenger ship cruise vacation"],
  ["⛴️", "ferry boat crossing"],
  ["🚀", "rocket launch space blast off"],
  ["🛰️", "satellite orbit space signal"],
  ["🌍", "earth globe world europe africa"],
  ["🌎", "earth globe americas world"],
  ["🌏", "earth globe asia australia world"],
  ["🗺️", "world map travel plan"],
  ["🧭", "compass navigate direction explore"],
  ["🏔️", "mountain snow peak climb"],
  ["🌋", "volcano lava eruption"],
  ["🏕️", "camping tent outdoors"],
  ["🏖️", "beach umbrella vacation sand sea"],
  ["🏝️", "desert island tropical paradise"],
  ["🌅", "sunrise morning dawn new day"],
  ["🌄", "sunrise mountains dawn"],
  ["🌇", "sunset city dusk evening"],
  ["🌃", "night stars city skyline"],
  ["🌌", "milky way galaxy space stars night"],
  ["🌉", "bridge night city"],
  ["🎡", "ferris wheel amusement park ride"],
  ["🎢", "roller coaster amusement thrill"],
  ["🗼", "tokyo tower landmark japan"],
  ["🗽", "statue liberty new york freedom"],
  ["🏰", "castle fairy tale medieval"],
  ["🏯", "japanese castle japan history"],
  ["🏨", "hotel stay travel"],
];

const OBJECTS: Row[] = [
  ["⌚", "watch time wrist"],
  ["📱", "phone mobile smartphone device"],
  ["💻", "laptop computer work code"],
  ["⌨️", "keyboard typing computer"],
  ["🖥️", "desktop computer monitor work"],
  ["🖨️", "printer paper office"],
  ["🖱️", "computer mouse click"],
  ["💾", "floppy disk save retro"],
  ["💿", "cd disc optical"],
  ["📷", "camera photo shutter picture"],
  ["📸", "camera flash photo shoot"],
  ["🎥", "movie camera film cinema"],
  ["📺", "tv television watch show"],
  ["📻", "radio broadcast music"],
  ["🎙️", "studio microphone podcast record"],
  ["🔋", "battery power charge full"],
  ["🪫", "low battery drained empty"],
  ["🔌", "plug power outlet electric"],
  ["💡", "light bulb idea bright invent"],
  ["🔍", "magnifying glass search find look"],
  ["🔎", "magnifying glass right search"],
  ["🔒", "lock secure locked private"],
  ["🔓", "unlock open unlocked"],
  ["🔐", "locked with key secure encrypted"],
  ["🔑", "key unlock access secret"],
  ["🗝️", "old key vintage"],
  ["🔨", "hammer tool build fix"],
  ["🪓", "axe chop wood"],
  ["🛠️", "tools wrench settings fix"],
  ["🔧", "wrench tool fix repair"],
  ["🔩", "bolt nut hardware"],
  ["⚙️", "gear settings cog machine"],
  ["🧲", "magnet attract physics"],
  ["💉", "syringe vaccine shot medical"],
  ["💊", "pill medicine capsule drug"],
  ["🩺", "stethoscope doctor health"],
  ["🚪", "door enter exit"],
  ["🪑", "chair sit furniture"],
  ["🛏️", "bed sleep rest"],
  ["🛋️", "couch sofa relax lounge"],
  ["🚿", "shower bath clean"],
  ["🧼", "soap wash clean hygiene"],
  ["🧹", "broom sweep clean"],
  ["🧺", "basket laundry"],
  ["🗑️", "trash bin delete rubbish"],
  ["📜", "scroll paper history parchment"],
  ["📄", "page document file paper"],
  ["📰", "newspaper news press"],
  ["📖", "open book read study"],
  ["📚", "books library study stack"],
  ["🔖", "bookmark save tag"],
  ["💼", "briefcase work business office"],
  ["📁", "folder files organize"],
  ["📅", "calendar date schedule"],
  ["📌", "pushpin pin location important"],
  ["📎", "paperclip attach clip file"],
  ["✂️", "scissors cut snip"],
  ["💰", "money bag cash rich"],
  ["💳", "credit card pay bank"],
  ["💎", "gem diamond precious luxury"],
  ["🔔", "bell notification ring alert"],
  ["🔕", "bell off mute silent"],
  ["📢", "loudspeaker announce megaphone shout"],
  ["📣", "megaphone cheer announcement"],
  ["⏰", "alarm clock wake time morning"],
  ["⏳", "hourglass waiting time sand"],
  ["🧸", "teddy bear plush toy cuddly"],
  ["🪄", "magic wand sparkle transform"],
  ["🔮", "crystal ball fortune mystic predict"],
  ["🧿", "nazar amulet protection evil eye"],
];

const SYMBOLS: Row[] = [
  ["✅", "check mark done yes complete approved"],
  ["☑️", "check box marked done"],
  ["✔️", "check heavy done correct"],
  ["❌", "cross no wrong x cancel"],
  ["❎", "cross button reject no"],
  ["⚠️", "warning caution alert careful"],
  ["🚫", "prohibited no forbidden banned stop"],
  ["🔞", "underage restricted adult"],
  ["♻️", "recycle green environment"],
  ["✳️", "eight spoked asterisk star"],
  ["❇️", "sparkle shine clean"],
  ["⚡", "lightning zap fast energy electric high voltage"],
  ["🔥", "fire lit hot flame burning"],
  ["🌈", "rainbow colors pride hope"],
  ["☀️", "sun sunny hot bright day"],
  ["⛅", "partly cloudy weather sun cloud"],
  ["☁️", "cloud weather overcast"],
  ["🌧️", "rain weather drops gloomy"],
  ["⛈️", "thunderstorm storm lightning rain"],
  ["❄️", "snowflake cold winter snow ice"],
  ["⛄", "snowman winter frosty"],
  ["🌊", "wave sea ocean water surf"],
  ["💧", "droplet water drip tear"],
  ["💥", "collision boom explosion crash"],
  ["💫", "dizzy stars sparkle woozy"],
  ["💯", "hundred points perfect score a hundred"],
  ["‼️", "double exclamation important"],
  ["⁉️", "interrobang surprise question"],
  ["❓", "question confused ask unknown"],
  ["❗", "exclamation important alert"],
  ["💤", "zzz sleep sleepy snore"],
  ["🌀", "cyclone swirl dizzy spin"],
  ["🆗", "ok button approve"],
  ["🆕", "new button fresh"],
  ["🆒", "cool button"],
  ["🆙", "up button level up"],
  ["🔝", "top arrow best"],
  ["🔗", "link chain url connect"],
  ["➕", "plus add more"],
  ["➖", "minus less remove"],
  ["➗", "divide math"],
  ["✖️", "multiply times close"],
  ["♾️", "infinity forever infinite endless"],
  ["🔱", "trident power pose"],
  ["⚜️", "fleur de lis royal france"],
  ["🛡️", "shield protect defense security"],
  ["⚖️", "balance scale justice law"],
];

const FLAGS: Row[] = [
  ["🏁", "checkered flag race finish start"],
  ["🚩", "red flag warning triangular mark"],
  ["🏴", "black flag"],
  ["🏳️", "white flag surrender peace"],
  ["🏳️‍🌈", "rainbow flag pride lgbtq"],
  ["🏳️‍⚧️", "transgender flag trans pride"],
  ["🏴‍☠️", "pirate flag skull jolly roger"],
  ["🇺🇳", "united nations un flag world"],
  ["🇪🇺", "european union eu flag europe"],
  ["🇺🇸", "united states usa america flag"],
  ["🇬🇧", "united kingdom uk britain flag"],
  ["🇨🇦", "canada maple flag"],
  ["🇧🇷", "brazil flag"],
  ["🇩🇪", "germany flag deutsch"],
  ["🇫🇷", "france flag french"],
  ["🇪🇸", "spain flag spanish"],
  ["🇮🇹", "italy flag italian"],
  ["🇯🇵", "japan flag japanese"],
  ["🇰🇷", "south korea flag korean"],
  ["🇮🇳", "india flag indian"],
  ["🇦🇺", "australia flag aussie"],
  ["🇳🇬", "nigeria flag"],
  ["🇿🇦", "south africa flag"],
  ["🇲🇽", "mexico flag mexican"],
  ["🇦🇷", "argentina flag"],
  ["🇵🇹", "portugal flag"],
];

function buildGroup(id: string, label: string, icon: string, rows: Row[]): EmojiGroup {
  const emojis: string[] = [];
  const keywords = new Map<string, string>();
  for (const [e, k] of rows) {
    if (!e) continue;
    emojis.push(e);
    if (k) keywords.set(e, k);
  }
  return { id, label, icon, emojis, keywords };
}

export const EMOJI_GROUPS: EmojiGroup[] = [
  buildGroup("smileys", "Smileys & People", "😀", SMILEYS),
  buildGroup("gestures", "Gestures", "👍", GESTURES),
  buildGroup("hearts", "Hearts & Love", "❤️", HEARTS),
  buildGroup("animals", "Animals & Nature", "🐻", ANIMALS),
  buildGroup("food", "Food & Drink", "🍕", FOOD),
  buildGroup("activities", "Activities", "🎉", ACTIVITIES),
  buildGroup("travel", "Travel & Places", "✈️", TRAVEL),
  buildGroup("objects", "Objects", "💡", OBJECTS),
  buildGroup("symbols", "Symbols", "✅", SYMBOLS),
  buildGroup("flags", "Flags", "🏁", FLAGS),
];

export const ALL_EMOJIS: string[] = EMOJI_GROUPS.flatMap((g) => g.emojis);

/** Local keyword search — never leaves the device (FR-MSG-006 discipline). */
export function searchEmojis(query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out: string[] = [];
  for (const g of EMOJI_GROUPS) {
    for (const e of g.emojis) {
      const hay = `${g.label} ${g.keywords.get(e) ?? ""}`;
      if (hay.toLowerCase().includes(q)) out.push(e);
      if (out.length >= 64) return out;
    }
  }
  return out;
}
